import { faPen, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { Activity, ActivityType } from '@spike/activity-model';
import {
    ACTIVITY_ICON_COLORS_MAP,
    ACTIVITY_ICON_FOREGROUND_COLORS_MAP,
    ACTIVITY_ICON_MAP,
    ACTIVITY_TITLE_MAP,
    HYDRATION_LABELS,
    RELIEF_CATEGORY_LABELS,
    RELIEF_SLOTS,
    REST_LABELS
} from 'components/Activities/Icon/ActivityIcon';
import { ActivityMedia } from 'components/Activities/Timeline/Media/ActivityMedia';
import { TimelineItemAction } from 'components/UI/V3/Timeline/Item/Action/TimelineItemAction';
import { TimelineItemHeader } from 'components/UI/V3/Timeline/Item/Header/TimelineItemHeader';
import { TimelineItemMeta } from 'components/UI/V3/Timeline/Item/Meta/TimelineItemMeta';
import { TimelineItem } from 'components/UI/V3/Timeline/Item/TimelineItem';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useReportBuilder } from '../ReportBuilderProvider';
import styles from './ReportActivityTimelineItem.module.scss';

const AT_HYDRATION = 'hydration' as ActivityType;
const AT_REST = 'rest' as ActivityType;
const AT_RELIEFS = 'reliefs' as ActivityType;

interface ReliefBucket {
    type: string;
    morning: number | null;
    afternoon: number | null;
    evening: number | null;
    note?: string;
}

interface ReportActivityTimelineItemProps {
    activity: Activity;
    /* eslint-disable-next-line no-unused-vars */
    onEdit: (activity: Activity) => void;
}

const ReportActivityTimelineItemComponent: React.FC<ReportActivityTimelineItemProps> = ({ activity, onEdit }) => {
    const { removeActivity } = useReportBuilder();

    const activitySubtypes = useSelector((state: RootState) => state.masterData.data.activitySubtypes);
    const masterData = useSelector((state: RootState) => state.masterData.data);
    const staffMembers = useSelector((state: RootState) => state.staff.staff);

    const activityType = activity.type;
    const icon = ACTIVITY_ICON_MAP[activityType];
    const iconColor = ACTIVITY_ICON_COLORS_MAP[activityType];
    const iconForegroundColor = ACTIVITY_ICON_FOREGROUND_COLORS_MAP[activityType];

    const singleSubtypeLabel = React.useMemo(() => {
        const value = activity.activitySubtype?.[0];
        if (typeof value !== 'string') return '';
        if (activityType === AT_HYDRATION) {
            return (HYDRATION_LABELS as Record<string, string>)[value] ?? value;
        }
        if (activityType === AT_REST) {
            return (REST_LABELS as Record<string, string>)[value] ?? value;
        }
        return '';
    }, [activity.activitySubtype, activityType]);

    const reliefBuckets = React.useMemo<ReliefBucket[]>(() => {
        if (activityType !== AT_RELIEFS) return [];
        return (activity.activitySubtype as unknown as ReliefBucket[]) ?? [];
    }, [activityType, activity.activitySubtype]);

    const subtypeLabelById = React.useMemo(() => {
        const options =
            activityType === ActivityType.INCIDENT
                ? activitySubtypes?.incident ?? []
                : activityType === ActivityType.ENRICHMENT
                ? activitySubtypes?.enrichment ?? []
                : [];

        return new Map(options.map(option => [option.id, option.name]));
    }, [activityType, activitySubtypes]);

    const activitySubtypeLabels = React.useMemo(
        () =>
            (activity.activitySubtype ?? [])
                .filter((subtype): subtype is string => typeof subtype === 'string')
                .map(subtypeId => subtypeLabelById.get(subtypeId) ?? subtypeId),
        [activity.activitySubtype, subtypeLabelById]
    );

    const activityInvolvedStaffs = (
        activity as Activity & { involvedStaffs?: Array<{ id: number; name: string; image?: string }> }
    ).involvedStaffs;

    const staffById = React.useMemo(() => {
        return new Map(
            staffMembers
                .filter(member => member.id)
                .map(member => [
                    member.id as number,
                    {
                        name: `${member.person.firstName} ${member.person.lastName}`,
                        image: member.person.avatar ?? undefined
                    }
                ])
        );
    }, [staffMembers]);

    const involvedStaffChips = React.useMemo(() => {
        if (activityInvolvedStaffs && activityInvolvedStaffs.length > 0) {
            return activityInvolvedStaffs.map(staff => ({
                id: staff.id,
                name: staff.name,
                image: staff.image ?? staffById.get(staff.id)?.image
            }));
        }

        return (activity.involvedStaffIds ?? []).map(staffId => ({
            id: staffId,
            name: staffById.get(staffId)?.name ?? `#${staffId}`,
            image: staffById.get(staffId)?.image
        }));
    }, [activityInvolvedStaffs, activity.involvedStaffIds, staffById]);

    const activityTitle = React.useMemo(() => {
        if (activityType === ActivityType.MEDICATION && activity.medicationMethodId != null) {
            const method = masterData.medicationMethodTypes?.find(m => m.id === activity.medicationMethodId);
            const unit = masterData.medicationDosageUnits?.find(u => u.id === activity.dosageUnitId);

            if (method && activity.dosageAmount != null && unit) {
                return `Medication · ${method.name} ${activity.dosageAmount} ${unit.name}`;
            }
        }

        return ACTIVITY_TITLE_MAP[activityType] ?? activityType;
    }, [activityType, activity, masterData]);

    const ateLabel = React.useMemo(() => {
        if (activityType !== ActivityType.FEEDING || !activity.ate) return null;
        return masterData.feedingAteValues?.find(v => v.id === activity.ate)?.name ?? null;
    }, [activityType, activity.ate, masterData]);

    const hasNotes = Boolean(activity.note);

    const editHandler = React.useCallback(() => onEdit(activity), [onEdit, activity]);

    const deleteHandler = React.useCallback(() => {
        removeActivity(activity);
    }, [removeActivity, activity]);

    const actions = React.useMemo(
        () => (
            <>
                <TimelineItemAction
                    icon={faTrashAlt}
                    onClick={deleteHandler}
                />
                <TimelineItemAction
                    icon={faPen}
                    onClick={editHandler}
                />
            </>
        ),
        [deleteHandler, editHandler]
    );

    return (
        <>
            <TimelineItem
                icon={icon}
                iconColor={iconColor}
                iconForegroundColor={iconForegroundColor}
                dateTime={activity.date}
                className={styles.root}
            >
                <TimelineItemHeader
                    title={activityTitle}
                    actions={actions}
                />

                <div className={styles.headerDivider} />

                {activityType === ActivityType.FEEDING && (
                    <>
                        <div className={styles.metaGrid}>
                            <TimelineItemMeta
                                title="Completed by"
                                description={activity.staffName}
                            />
                            <TimelineItemMeta
                                title="Date & Time"
                                description={activity.date.format('MMM D, YYYY h:mma')}
                            />
                            <TimelineItemMeta
                                title="Ate"
                                description={ateLabel ?? '–'}
                            />
                        </div>
                        {hasNotes && (
                            <TimelineItemMeta
                                title="Notes"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                {activityType === ActivityType.MEDICATION && (
                    <>
                        <TimelineItemMeta
                            title="Completed by"
                            description={activity.staffName}
                        />
                        {hasNotes && (
                            <TimelineItemMeta
                                title="Notes"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                {activityType === AT_HYDRATION && (
                    <TimelineItemMeta
                        title="Water Intake"
                        description={singleSubtypeLabel}
                    />
                )}

                {activityType === AT_REST && (
                    <TimelineItemMeta
                        title="Have they rested?"
                        description={singleSubtypeLabel}
                    />
                )}

                {activityType === AT_RELIEFS &&
                    reliefBuckets.map((bucket, idx) => (
                        <div
                            key={`${bucket.type}-${idx}`}
                            className={styles.reliefBlock}
                        >
                            <p className={styles.reliefTitle}>
                                {(RELIEF_CATEGORY_LABELS as Record<string, string>)[bucket.type] ?? bucket.type}
                            </p>
                            <div className={styles.reliefTable}>
                                <div className={styles.reliefTableHeader}>
                                    <span>Time of Day</span>
                                    <span>How Many Times?</span>
                                </div>
                                {RELIEF_SLOTS.map(slot => ({ label: slot.label, count: bucket[slot.key] }))
                                    .filter(({ count }) => count !== null && count !== undefined)
                                    .map(({ label, count }) => (
                                        <div
                                            key={label}
                                            className={styles.reliefTableRow}
                                        >
                                            <span>{label}</span>
                                            <span>{count}</span>
                                        </div>
                                    ))}
                            </div>
                            {bucket.note && (
                                <TimelineItemMeta
                                    title="Notes"
                                    description={bucket.note}
                                />
                            )}
                        </div>
                    ))}

                {activityType === ActivityType.INCIDENT && (
                    <>
                        <TimelineItemMeta
                            title="Incident Type"
                            description={activitySubtypeLabels.join(', ')}
                        />

                        {involvedStaffChips.length > 0 && (
                            <TimelineItemMeta title="Staff Involved">
                                <div className={styles.staffChips}>
                                    {involvedStaffChips.map((staff, index) => (
                                        <span
                                            key={`incident-staff-${index}-${staff.id}`}
                                            className={styles.staffChip}
                                        >
                                            {staff.image && (
                                                <img
                                                    className={styles.staffAvatar}
                                                    src={staff.image}
                                                    alt={staff.name}
                                                />
                                            )}
                                            {staff.name}
                                        </span>
                                    ))}
                                </div>
                            </TimelineItemMeta>
                        )}

                        {hasNotes && (
                            <TimelineItemMeta
                                title="Incident Description"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                {activityType === ActivityType.ENRICHMENT && (
                    <>
                        <TimelineItemMeta
                            title="Activity Type"
                            description={activitySubtypeLabels.join(', ')}
                        />

                        {involvedStaffChips.length > 0 && (
                            <TimelineItemMeta title="Staff">
                                <div className={styles.staffChips}>
                                    {involvedStaffChips.map((staff, index) => (
                                        <span
                                            key={`enrichment-staff-${index}-${staff.id}`}
                                            className={styles.staffChip}
                                        >
                                            {staff.image && (
                                                <img
                                                    className={styles.staffAvatar}
                                                    src={staff.image}
                                                    alt={staff.name}
                                                />
                                            )}
                                            {staff.name}
                                        </span>
                                    ))}
                                </div>
                            </TimelineItemMeta>
                        )}

                        {hasNotes && (
                            <TimelineItemMeta
                                title="Activity Notes"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                <ActivityMedia
                    activity={activity}
                    showDivider={activityType === ActivityType.INCIDENT || activityType === ActivityType.ENRICHMENT}
                    galleryClassName={styles.gallery}
                />

                {hasNotes &&
                    activityType !== AT_RELIEFS &&
                    activityType !== ActivityType.INCIDENT &&
                    activityType !== ActivityType.ENRICHMENT &&
                    activityType !== ActivityType.FEEDING &&
                    activityType !== ActivityType.MEDICATION && (
                        <TimelineItemMeta
                            title="Notes"
                            description={activity.note}
                        />
                    )}
            </TimelineItem>
        </>
    );
};

export const ReportActivityTimelineItem = React.memo(ReportActivityTimelineItemComponent);
